import axios from "axios";
import { isPlatformHostname as hostIsPlatform } from "./platformHost";

// Resolve the API base URL.
//  - On the platform / preview host we use REACT_APP_BACKEND_URL (the platform
//    backend).
//  - On a merchant's OWN custom domain the SPA and the API are served by the
//    SAME origin (Caddy proxies /api -> backend on every domain), so we must
//    call the API same-origin. Using the baked platform URL there would be a
//    cross-origin request that the browser blocks (CORS + cross-site cookies),
//    which made the storefront fail to load and fall back to the marketing
//    landing page.
function envBackendHost() {
  try {
    return new URL(process.env.REACT_APP_BACKEND_URL || "").hostname.toLowerCase();
  } catch (e) {
    return "";
  }
}

/** Hostname shown in merchant UI (follows REACT_APP_BACKEND_URL). */
export function platformDisplayHost() {
  return envBackendHost() || (typeof window !== "undefined" ? window.location.hostname : "");
}

/** True when this hostname is the SaaS platform (not a merchant custom domain). */
export function isPlatformHostname(host) {
  return hostIsPlatform(host, envBackendHost());
}

function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return envUrl;
  try {
    const host = (window.location.hostname || "").toLowerCase();
    // Custom domain -> talk to the API same-origin (served by Caddy here).
    if (!isPlatformHostname(host)) return "";
  } catch (e) { /* fall back to envUrl */ }
  return envUrl;
}

export const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

/** Platform API origin from build env (set even on custom domains, where BACKEND_URL is ""). */
export function platformApiOrigin() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function extractErr(e) {
  const proxyFail = /cloudflare|origin web server|invalid or incomplete response|error 520|502 bad gateway/i;
  const friendlyProxy = "AI Store Manager could not reach Grok in time. Please try that command again.";
  const d = e?.response?.data?.detail;
  if (typeof d === "string") {
    if (proxyFail.test(d) || d.includes("<html")) return friendlyProxy;
    return d;
  }
  if (Array.isArray(d))
    return d
      .map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x)))
      .join(", ");
  if (d && typeof d === "object") {
    if (typeof d.message === "string") return d.message;
    if (typeof d.msg === "string") return d.msg;
    if (typeof d.detail === "string") return d.detail;
  }
  const status = e?.response?.status;
  const body = e?.response?.data;
  if (typeof body === "string") {
    const looksCf = proxyFail.test(body);
    if (body.startsWith("<") || body.includes("<html") || looksCf) {
      return friendlyProxy;
    }
  }
  if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
    return friendlyProxy;
  }
  if (status >= 500) return "Something went wrong on our side. Please try again shortly.";
  return e?.message || "Something went wrong";
}

export function mediaUrl(url) {
  // Turn stored image values into a fetchable src.
  //  - https://media.merastartup.in/... (R2) and other absolute URLs pass through
  //  - /api/media/... (legacy local disk) is prefixed with the API origin
  //  - relative leftovers like "images/wrap-dress.webp" MUST NOT be returned
  //    as-is: the browser would resolve them against the product page
  //    (/clothings/product/images/...) and the image 404s. Route those
  //    through the media proxy instead.
  if (!url) return null;
  const u = String(url).trim();
  if (!u) return null;
  const lower = u.toLowerCase();
  if (lower.startsWith("http://") || lower.startsWith("https://") || lower.startsWith("data:")) return u;
  if (u.startsWith("//")) return `https:${u}`;
  const withSlash = u.startsWith("/") ? u : `/${u}`;
  if (withSlash.startsWith("/api/")) return `${BACKEND_URL}${withSlash}`;
  return `${BACKEND_URL}/api/media${withSlash}`;
}

/** URL <model-viewer> can fetch with CORS. R2 CDN links are rewritten to /api/media. */
export function modelMediaUrl(url) {
  if (!url) return null;
  const raw = String(url).trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  const marker = "/api/media/";
  const idx = lower.indexOf(marker);
  if (idx >= 0) {
    const rest = raw.slice(idx + marker.length);
    return mediaUrl(`/api/media/${rest}`);
  }
  try {
    const abs = lower.startsWith("http://") || lower.startsWith("https://") ? raw : mediaUrl(raw);
    const u = new URL(abs);
    const mediaHosts = ["media.merastartup.in", "media.gro.codes"];
    if (mediaHosts.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`))) {
      const key = u.pathname.replace(/^\//, "");
      if (key) return mediaUrl(`/api/media/${key}`);
    }
  } catch { /* fall through */ }
  return mediaUrl(raw);
}

// Public URL of a merchant store, used by the admin panel "view store / view page"
// links. Once the merchant has a VERIFIED custom domain, links point to their own
// brand domain (clean URLs); otherwise they fall back to the platform slug URL.
export function storePublicBase(tenant) {
  const cd = (tenant?.custom_domain || "").replace(/^https?:\/\//, "").split("/")[0].toLowerCase();
  if (cd && tenant?.custom_domain_verified) return `https://${cd}`;
  const platform = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${platform}/${tenant?.slug || ""}`;
}

/** Platform slug URL → verified custom domain (path after /{slug} is kept). */
export function slugStorefrontRedirectUrl(tenant, { slug, pathname, search = "", hash = "", hostname = "" } = {}) {
  const raw = (tenant?.custom_domain || "").replace(/^https?:\/\//, "").split("/")[0].toLowerCase();
  if (!raw || !tenant?.custom_domain_verified) return null;
  const apex = raw.replace(/^www\./, "");
  const host = String(hostname || "").toLowerCase().split(":")[0];
  if (host === raw || host === apex || host === `www.${apex}`) return null;
  const prefix = `/${slug || tenant?.slug || ""}`;
  let path = pathname || "/";
  if (prefix.length > 1 && (path === prefix || path.startsWith(`${prefix}/`))) {
    path = path.slice(prefix.length) || "/";
  }
  if (!path.startsWith("/")) path = `/${path}`;
  return `https://${raw}${path}${search || ""}${hash || ""}`;
}

export function storePageUrl(tenant, pageSlug) {
  return `${storePublicBase(tenant)}/page/${pageSlug}`;
}

export default api;
